import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMessageCircle, FiBookmark, FiShare2 } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import LoginPrompt from '../LoginPrompt/LoginPrompt';
import { favoritesAPI, reactionsAPI } from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';
import { REACTIONS } from '../../utils/constants';
import toast from 'react-hot-toast';
import './MemeCard.css';

const MemeCard = ({ post }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [userReaction, setUserReaction] = useState(null);
  const [reactionCounts, setReactionCounts] = useState(post?.reactions || {});
  const [isSaved, setIsSaved] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    const nextCounts = post?.reactions || {};
    // fallback legacy: likes -> love
    if (Object.keys(nextCounts).length === 0 && Number.isFinite(post?.likes)) {
      setReactionCounts({ love: post.likes });
      return;
    }
    setReactionCounts(nextCounts);
  }, [post?.reactions, post?.likes]);

  useEffect(() => {
    const loadState = async () => {
      if (!post?.id) return;
      if (!isAuthenticated) {
        setUserReaction(null);
        return;
      }
      try {
        const [favRes, reactRes] = await Promise.all([
          favoritesAPI.check(post.id).catch(() => ({ data: {} })),
          reactionsAPI.getUserReaction(post.id).catch(() => ({ data: { userReaction: null } }))
        ]);
        setIsSaved(Boolean(favRes.data?.isFavorited ?? favRes.data?.isFavorite));
        setUserReaction(reactRes.data?.userReaction || null);
      } catch (e) {
        // silent
      }
    };

    loadState();
  }, [isAuthenticated, post?.id]);

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getMemeSlug = () => {
    if (post?.slug) return post.slug;
    const base = slugify(post?.title) || 'meme';
    return post?.id ? `${base}-${post.id}` : base;
  };

  const handleCardClick = (e) => {
    if (e.target.closest('a') || e.target.closest('button')) {
      return;
    }
    // Navigate to meme page
    navigate(`/meme/${getMemeSlug()}`);
  };

  const formatCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const commentsCount = post?.comments ?? post?.commentsCount ?? post?.comments_count ?? 0;

  const handleReaction = (reactionId) => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    (async () => {
      try {
        const res = await reactionsAPI.toggle(post.id, reactionId);
        setUserReaction(res.data?.userReaction || null);
        setReactionCounts(res.data?.reactions || {});
      } catch (error) {
        console.error('Error toggling reaction:', error);
        toast.error('No se pudo reaccionar');
      }
    })();
  };

  return (
    <>
      <article className="meme-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
        <Link to={`/meme/${getMemeSlug()}`} className="meme-image-container" onClick={(e) => e.stopPropagation()}>
          <img 
            src={resolveMediaUrl(post.image)} 
            alt={post.title}
            className="meme-image"
          />
        </Link>

        <div className="meme-overlay">
          <div className="meme-actions">
            {REACTIONS.map((r) => (
              <button
                key={r.id}
                className={`meme-action-btn meme-reaction-btn ${userReaction === r.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReaction(r.id);
                }}
                title={r.label}
                type="button"
              >
                <span className="meme-reaction-emoji">{r.emoji}</span>
                <span>{formatCount(reactionCounts?.[r.id] || 0)}</span>
              </button>
            ))}

            <button className="meme-action-btn" onClick={(e) => {
              e.stopPropagation();
              navigate(`/meme/${getMemeSlug()}`);
            }}>
              <FiMessageCircle />
              <span>{formatCount(commentsCount)}</span>
            </button>

            <button 
              className={`meme-action-btn ${isSaved ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!isAuthenticated) {
                  setShowLoginPrompt(true);
                  return;
                }
                (async () => {
                  try {
                    if (isSaved) {
                      await favoritesAPI.remove(post.id);
                      setIsSaved(false);
                      toast.success('Eliminado de guardados');
                    } else {
                      await favoritesAPI.add(post.id);
                      setIsSaved(true);
                      toast.success('Guardado');
                    }
                  } catch (error) {
                    console.error('Error toggling favorite:', error);
                    toast.error('No se pudo guardar');
                  }
                })();
              }}
            >
              <FiBookmark />
            </button>

            <button 
              className="meme-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                const url = `${window.location.origin}/meme/${getMemeSlug()}`;
                if (navigator.share) {
                  navigator.share({ title: post.title, url });
                } else {
                  navigator.clipboard.writeText(url);
                }
              }}
            >
              <FiShare2 />
            </button>
          </div>

          <Link
            to={`/${post.author?.username || post.author?.id}`}
            className="meme-author"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={resolveMediaUrl(post.author?.avatar)} 
              alt={post.author?.name}
              className="meme-author-avatar"
            />
            <span className="meme-author-name">
              {post.author?.name || 'Usuario'}
              {post.author?.verification_level && post.author.verification_level !== 'none' && (
                <MdVerified
                  style={{
                    marginLeft: 6,
                    verticalAlign: 'middle',
                    color: post.author.badge_color || 'currentColor'
                  }}
                  title={post.author.badge_label || 'Cuenta verificada'}
                />
              )}
            </span>
          </Link>
        </div>

        <Link to={`/meme/${getMemeSlug()}`} className="meme-title" onClick={(e) => e.stopPropagation()}>
          {post.title}
        </Link>
      </article>

      {showLoginPrompt && (
        <LoginPrompt 
          message="Inicia sesión para interactuar con este meme"
          onClose={() => setShowLoginPrompt(false)}
        />
      )}
    </>
  );
};

export default MemeCard;
